import { useMemo } from 'react';
import { useAuthStore } from '@/store/authStore';
import type {
  ClassCategory,
  ProfileClassOrganizationLayout,
} from '@/features/profile/classes/classCategoryTypes';
import {
  buildLayoutFromBoardWithCategory,
  buildLayoutFromRawUser,
} from '@/features/profile/classes/profileClassesLayout';
import type { OwnedClassCard } from '@/features/profile/types';

export { buildLayoutFromRawUser, buildLayoutFromBoardWithCategory };

/**
 * React hook — returns a `ProfileClassOrganizationLayout` built from the
 * logged-in user's `boardWithCategory[]` array, re-computing when the
 * persisted login payload changes.
 */
export function useProfileClassesFromLogin(): ProfileClassOrganizationLayout {
  const rawUser = useAuthStore((state) => state.rawUser);
  return useMemo(() => buildLayoutFromRawUser(rawUser), [rawUser]);
}

/** Editable draft + flat card list derived from the persisted login layout. */
export interface ProfileClassesDraftFromLogin {
  readonly allCards: readonly OwnedClassCard[];
  readonly categories: readonly ClassCategory[];
  readonly uncategorizedClassIds: readonly string[];
}

function buildDraftFromLayout(
  layout: ProfileClassOrganizationLayout,
): ProfileClassesDraftFromLogin {
  const allCards: OwnedClassCard[] = [
    ...layout.uncategorized,
    ...layout.categories.flatMap((group) => group.classes),
  ];
  const categories = layout.categories.map((group) => ({
    id: group.category.id,
    name: group.category.name,
    classIds: group.classes.map((card) => card.id),
    sortOrder: group.category.sortOrder,
  }));
  const uncategorizedClassIds = layout.uncategorized.map((card) => card.id);
  return { allCards, categories, uncategorizedClassIds };
}

export function useProfileClassesDraftFromLogin(): ProfileClassesDraftFromLogin {
  const rawUser = useAuthStore((state) => state.rawUser);
  return useMemo(() => buildDraftFromLayout(buildLayoutFromRawUser(rawUser)), [rawUser]);
}

/** Non-React variant for selectors / stores. */
export function getProfileClassesFromLogin(): ProfileClassOrganizationLayout {
  return buildLayoutFromRawUser(useAuthStore.getState().rawUser);
}
